"use strict";
/**
 * Dictation Processing Service
 * Core business logic for dictation handling
 */
Object.defineProperty(exports, "__esModule", { value: true });
exports.dictationProcessingService = exports.DictationProcessingService = void 0;
const dictationValidation_1 = require("../../rules/dictationValidation");
const textTokenizer_1 = require("../../utils/textTokenizer");
/**
 * Dictation service class
 */
class DictationProcessingService {
    /**
     * Prepare dictation session
     */
    prepareDictationSession(config) {
        // Validate dictation text
        const difficulty = this.calculateDictationDifficulty(config.level);
        const validation = (0, dictationValidation_1.validateDictation)(config.content, difficulty, config.language);
        // Split text into sentences for dictation
        const sentences = (0, textTokenizer_1.splitIntoSentences)(config.content, config.language);
        // Get optimal settings for language
        const _baseSettings = (0, dictationValidation_1.getOptimalDictationSettings)(config.language);
        const settings = {
            preferredVoiceId: config.voiceId,
            pauseBetweenWords: config.settings?.pauseBetweenWords ?? 1000,
            pauseBetweenSentences: config.settings?.pauseBetweenSentences ?? 2000,
            playbackSpeed: config.settings?.playbackSpeed ?? 1.0,
            autoRepeat: config.settings?.autoRepeat ?? false,
            timeoutPerSentence: config.settings?.timeoutPerSentence ?? 30000,
        };
        // Create session object
        const session = {
            userId: config.userId,
            textId: config.textId,
            voiceId: config.voiceId || "",
            sentences,
            totalSentences: sentences.length,
            settings,
        };
        return {
            session,
            sentences,
            settings,
            validation,
        };
    }
    /**
     * Process dictation submission
     */
    processDictationSubmission(submission, expectedContent, language, level) {
        const difficulty = this.calculateDictationDifficulty(level);
        const comparisonOptions = (0, dictationValidation_1.getOptimalDictationSettings)(language);
        // Split expected content into sentences
        const expectedSentences = (0, textTokenizer_1.splitIntoSentences)(expectedContent, language);
        // Use sentence-by-sentence input if available, otherwise split user input
        const userSentences = submission.userInputsBySentence &&
            submission.userInputsBySentence.length > 0
            ? submission.userInputsBySentence
            : (0, textTokenizer_1.splitIntoSentences)(submission.userInput, language);
        // Process each sentence
        const sentenceResults = [];
        let totalCorrectWords = 0;
        let totalWords = 0;
        const maxSentences = Math.max(expectedSentences.length, userSentences.length);
        for (let i = 0; i < maxSentences; i++) {
            const expected = expectedSentences[i] || "";
            const actual = userSentences[i] || "";
            if (expected || actual) {
                const result = (0, dictationValidation_1.checkDictation)(expected, actual, difficulty, comparisonOptions);
                const wordCount = (0, textTokenizer_1.countWords)(expected, language);
                const correctWords = Math.round(result.similarity * wordCount);
                sentenceResults.push({
                    expected,
                    actual,
                    accuracy: result.similarity,
                    correctWords,
                    totalWords: wordCount,
                });
                totalCorrectWords += correctWords;
                totalWords += wordCount;
            }
        }
        // Calculate overall metrics
        const baseAccuracy = totalWords > 0 ? totalCorrectWords / totalWords : 0;
        const averageSimilarity = sentenceResults.length > 0
            ? sentenceResults.reduce((sum, r) => sum + r.accuracy, 0) /
                sentenceResults.length
            : 0;
        // Adjust accuracy based on repeats (more repeats = slight penalty)
        const totalRepeats = Object.values(submission.sentenceRepeats).reduce((sum, repeats) => sum + repeats, 0);
        const repeatPenalty = Math.min(totalRepeats * 0.02, 0.1); // Max 10% penalty
        const finalAccuracy = Math.max(0, baseAccuracy - repeatPenalty);
        // Calculate completion rate
        const expectedLength = expectedSentences.length;
        const actualLength = userSentences.filter((s) => s.trim().length > 0).length;
        const completionRate = expectedLength > 0 ? actualLength / expectedLength : 0;
        return {
            sessionId: submission.sessionId,
            accuracy: baseAccuracy,
            finalAccuracy,
            totalRepeats,
            timeSpentMs: submission.timeSpentMs,
            sentenceResults,
            overallStats: {
                correctWords: totalCorrectWords,
                totalWords,
                averageSimilarity,
                completionRate,
            },
        };
    }
    /**
     * Calculate text difficulty for dictation
     */
    calculateTextDifficulty(content, language) {
        const sentences = (0, textTokenizer_1.splitIntoSentences)(content, language);
        const wordCount = (0, textTokenizer_1.countWords)(content, language);
        const sentenceCount = sentences.length;
        const averageWordsPerSentence = sentenceCount > 0 ? wordCount / sentenceCount : 0;
        // Calculate difficulty based on metrics
        let difficulty = "EASY";
        let recommendedLevel = "BEGINNER";
        if (wordCount > 100 || averageWordsPerSentence > 15) {
            difficulty = "HARD";
            recommendedLevel = "ADVANCED";
        }
        else if (wordCount > 50 || averageWordsPerSentence > 10) {
            difficulty = "MEDIUM";
            recommendedLevel = "INTERMEDIATE";
        }
        return {
            wordCount,
            sentenceCount,
            averageWordsPerSentence,
            estimatedDifficulty: difficulty,
            recommendedLevel,
        };
    }
    /**
     * Generate dictation statistics
     */
    calculateDictationStats(results) {
        if (results.length === 0) {
            return {
                totalSessions: 0,
                averageAccuracy: 0,
                totalTimeSpent: 0,
                improvementRate: 0,
                streakDays: 0,
                sessionsByDifficulty: { EASY: 0, MEDIUM: 0, HARD: 0 },
            };
        }
        const totalSessions = results.length;
        const averageAccuracy = results.reduce((sum, r) => sum + r.finalAccuracy, 0) / totalSessions;
        const totalTimeSpent = results.reduce((sum, r) => sum + r.timeSpentMs, 0);
        // Calculate improvement rate (comparing first and last 3 sessions)
        let improvementRate = 0;
        if (results.length >= 6) {
            const firstThree = results.slice(0, 3);
            const lastThree = results.slice(-3);
            const firstAverage = firstThree.reduce((sum, r) => sum + r.finalAccuracy, 0) / 3;
            const lastAverage = lastThree.reduce((sum, r) => sum + r.finalAccuracy, 0) / 3;
            improvementRate = lastAverage - firstAverage;
        }
        return {
            totalSessions,
            averageAccuracy,
            totalTimeSpent,
            improvementRate,
            streakDays: 0, // Would need date information to calculate
            sessionsByDifficulty: { EASY: 0, MEDIUM: 0, HARD: 0 }, // Would need difficulty info
        };
    }
    /**
     * Suggest next dictation settings
     */
    suggestNextDictationSettings(userLevel, recentResults, currentSettings) {
        if (recentResults.length === 0) {
            return {};
        }
        const recentAccuracy = recentResults.slice(-3).reduce((sum, r) => sum + r.finalAccuracy, 0) /
            Math.min(3, recentResults.length);
        const suggestions = {};
        // Adjust playback speed based on accuracy
        if (recentAccuracy > 0.9) {
            // High accuracy - can increase speed
            suggestions.playbackSpeed = Math.min(currentSettings.playbackSpeed + 0.1, 2.0);
        }
        else if (recentAccuracy < 0.7) {
            // Low accuracy - decrease speed
            suggestions.playbackSpeed = Math.max(currentSettings.playbackSpeed - 0.1, 0.5);
        }
        // Adjust pauses based on level and accuracy
        if (userLevel === "BEGINNER" || userLevel === "ELEMENTARY") {
            if (recentAccuracy < 0.8) {
                suggestions.pauseBetweenWords = Math.max(currentSettings.pauseBetweenWords, 1500);
                suggestions.pauseBetweenSentences = Math.max(currentSettings.pauseBetweenSentences, 3000);
            }
        }
        // Enable auto-repeat for struggling users
        if (recentAccuracy < 0.6) {
            suggestions.autoRepeat = true;
        }
        else if (recentAccuracy > 0.9) {
            suggestions.autoRepeat = false;
        }
        return suggestions;
    }
    /**
     * Map level to dictation difficulty
     */
    calculateDictationDifficulty(level) {
        switch (level) {
            case "BEGINNER":
            case "ELEMENTARY":
                return "EASY";
            case "INTERMEDIATE":
            case "UPPER_INTERMEDIATE":
                return "MEDIUM";
            case "ADVANCED":
            case "PROFICIENT":
                return "HARD";
            default:
                return "MEDIUM";
        }
    }
}
exports.DictationProcessingService = DictationProcessingService;
// Create singleton instance
exports.dictationProcessingService = new DictationProcessingService();
//# sourceMappingURL=dictationProcessingService.js.map